import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { fetchJson, ApiError } from "@/lib/api";

// Phase 2a customer-accounts FR-5 — request a password reset email. The
// response is always 202 from the server regardless of whether the
// address is on file, so we present the same confirmation either way.
export function PasswordResetPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await fetchJson("/api/v1/auth/password_reset", {
        method: "POST",
        body: { email: email.trim().toLowerCase() }
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError("Too many reset requests from this network. Try again in an hour.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="mx-auto max-w-md space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-black">Reset your password</h1>
        <p className="mt-1 text-sm text-zinc-600">
          We&rsquo;ll email you a link to set a new password.
        </p>
      </header>

      {submitted ? (
        <Alert variant="success" role="status">
          If that email is on file, you&rsquo;ll receive reset instructions shortly.
          The link expires in 6 hours.
        </Alert>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4 rounded-md border border-zinc-200 bg-brand-white p-5">
          {error ? (
            <Alert variant="destructive" role="alert">
              {error}
            </Alert>
          ) : null}
          <FormField id="email" label="Email" required>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormField>
          <Button type="submit" disabled={pending} className="w-full">
            {pending ? "Sending…" : "Send reset email"}
          </Button>
        </form>
      )}

      <p className="text-center text-sm text-zinc-600">
        <Link to="/sign-in" className="text-brand-red underline">
          Back to sign in
        </Link>
      </p>
    </section>
  );
}
